import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  AlertCircle,
  FileQuestion,
  Database,
  Send,
  Lock,
  Unlock,
  Check,
  Flag,
  Clock,
  FileText,
  Users,
  DollarSign,
  ClipboardList,
  AlertTriangle,
  Eye,
} from "lucide-react";

import {
  Card,
  PageHeader,
  StatCard,
  Tabs,
  Badge,
  Progress,
  useToast,
  formatDate,
} from "../adminComponents/adminComponents";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useAdminDataCollectionHook } from "../../hooks/useAdminHooks";
import { useManagerDataCollectionHook } from "../../hooks/useManagerHooks";
import "./dataCollection.css";

const TABS = [
  { value: "streams", label: "Data Streams" },
  { value: "surveys", label: "Survey Management" },
  { value: "validation", label: "Validation & Quality" },
];

const ICONS_BY_NAME = {
  "check-circle": CheckCircle,
  "alert-circle": AlertCircle,
  "file-question": FileQuestion,
  database: Database,
  "dollar-sign": DollarSign,
  users: Users,
  "clipboard-list": ClipboardList,
  "file-text": FileText,
};

const DEFAULT_DATA_COLLECTION = {
  reporting: {
    period: "-",
    reporting_locked: false,
  },
  summary_cards: [],
  data_streams: {
    summary_cards: [],
    overdue_updates: [],
  },
  surveys: [],
  venture_data_validation: [],
  audit_trail: [],
};

function severityBadge(sev) {
  if (sev === "error") return "danger";
  if (sev === "warning") return "warning";
  return "primary";
}

export default function DataCollection() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast, ToastViewport } = useToast();
  const [tab, setTab] = useState("streams");
  const [reportingLockedOverride, setReportingLockedOverride] = useState(null);

  const workspaceBase = user?.role === ROLES.ADMIN ? "/admin" : "/manager";
  const isAdmin = user?.role === ROLES.ADMIN;
  const isManager = user?.role === ROLES.MANAGER;

  const adminDataCollectionQuery = useAdminDataCollectionHook({
    token,
    userId: user?.id,
    enabled: isAdmin,
  });

  const managerDataCollectionQuery = useManagerDataCollectionHook({
    token,
    userId: user?.id,
    enabled: isManager,
  });

  const apiData = isManager
    ? managerDataCollectionQuery.data
    : adminDataCollectionQuery.data;
  const isLoading = isManager
    ? managerDataCollectionQuery.isLoading
    : adminDataCollectionQuery.isLoading;

  const dataCollection = apiData ?? DEFAULT_DATA_COLLECTION;
  const summaryCards = dataCollection?.summary_cards ?? [];
  const streamCards = dataCollection?.data_streams?.summary_cards ?? [];
  const overdueUpdates = dataCollection?.data_streams?.overdue_updates ?? [];
  const surveys = dataCollection?.surveys ?? [];
  const validationFlags = dataCollection?.venture_data_validation ?? [];
  const auditTrail = dataCollection?.audit_trail ?? [];

  const apiReportingLocked = Boolean(
    dataCollection?.reporting?.reporting_locked,
  );
  const reportingLocked = reportingLockedOverride ?? apiReportingLocked;

  // -- handlers --
  const handleSendReminders = () =>
    toast({
      title: "Reminders Sent",
      description: `Data collection reminders sent to ${overdueUpdates.length} ventures with missing submissions.`,
    });

  const handleToggleLock = () => {
    setReportingLockedOverride((current) =>
      current == null ? !apiReportingLocked : !current,
    );
    toast({
      title: reportingLocked
        ? "Reporting Period Unlocked"
        : "Reporting Period Locked",
      description: reportingLocked
        ? "Ventures can now submit data for this period."
        : "Data submissions are now locked for this period.",
    });
  };

  const handleApprove = () =>
    toast({
      title: "Submission Approved",
      description: "The flagged submission has been approved.",
    });

  const handleFlag = () =>
    toast({
      title: "Submission Flagged",
      description: "The submission has been flagged for review.",
      tone: "danger",
    });

  if (isLoading && (isAdmin || isManager)) {
    return (
      <div className="adminDataCollection-root">
        <PageHeader
          title="Data Collection"
          description="Manage, track, and validate all incoming data across the organization"
        />
        <p className="admin-muted">Loading data collection metrics...</p>
      </div>
    );
  }

  return (
    <div className="adminDataCollection-root">
      <PageHeader
        title="Data Collection"
        description="Manage, track, and validate all incoming data across the organization"
      />

      <div className="admin-grid admin-grid-4">
        {summaryCards.map((card, index) => {
          const Icon = ICONS_BY_NAME[card.icon] ?? Database;
          return (
            <StatCard
              key={`${card.title}-${index}`}
              title={card.title}
              value={card.value}
              icon={Icon}
              description={card.label}
            />
          );
        })}
      </div>


      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      {tab === "streams" && (
        <div className="adminDataCollection-streamSection">
          <div className="admin-grid admin-grid-4">
            {streamCards.map((card, index) => {
              const Icon = ICONS_BY_NAME[card.icon] ?? Database;
              return (
                <StreamCard
                  key={`${card.title}-${index}`}
                  icon={Icon}
                  tone={streamTone(card.title)}
                  title={card.title}
                  value={`${card.submitted ?? 0} / ${card.total ?? 0}`}
                  progress={Number(card.progress ?? 0)}
                  note={card.label ?? "-"}
                />
              );
            })}
          </div>

          <Card
            title="Ventures Needing Data Updates"
            description="Ventures with missing or stale data submissions"
          >
            <div className="admin-grid admin-grid-2">
              {overdueUpdates.map((venture) => (
                <button
                  key={venture.venture_id}
                  type="button"
                  className="adminDataCollection-needsCard"
                  onClick={() =>
                    navigate(`${workspaceBase}/ventures/${venture.venture_id}`)
                  }
                >
                  <div>
                    <p className="adminDataCollection-needsName">
                      {venture.venture_name}
                    </p>
                    <p className="admin-muted adminDataCollection-needsMeta">
                      Last update:{" "}
                      {venture.last_update_date
                        ? formatDate(venture.last_update_date)
                        : "Never"}
                    </p>
                  </div>
                  <Badge tone="warning">
                    <Clock className="adminDataCollection-badgeIcon" />
                    {venture.status ?? "Overdue"}
                  </Badge>
                </button>
              ))}
              {overdueUpdates.length === 0 && (
                <p className="admin-muted">
                  No stale data — all ventures are up to date.
                </p>
              )}
            </div>
          </Card>
        </div>
      )}

      {tab === "surveys" && (
        <Card
          title="Survey Completion Status"
          description="Track baseline, midline, and endline survey completion by cohort"
        >
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Survey Type</th>
                  <th>Cohort</th>
                  <th>Assigned</th>
                  <th>Completed</th>
                  <th>Completion Rate</th>
                  <th>Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {surveys.map((survey, index) => {
                  const pct = Number(survey.completion_rate ?? 0);
                  return (
                    <tr key={index}>
                      <td>
                        <Badge tone="outline">{survey.survey_type}</Badge>
                      </td>
                      <td>{survey.cohort}</td>
                      <td>{survey.assigned}</td>
                      <td>{survey.completed}</td>
                      <td>
                        <div className="adminDataCollection-progressCell">
                          <div className="adminDataCollection-progressBar">
                            <Progress value={pct} tone="success" />
                          </div>
                          <span>{pct.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="admin-muted">
                        {formatDate(survey.last_updated)}
                      </td>
                    </tr>
                  );
                })}
                {surveys.length === 0 && (
                  <tr>
                    <td colSpan="6" className="admin-muted">
                      No survey data available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "validation" && (
        <>
          <Card
            title={
              <span className="adminDataCollection-titleRow">
                <AlertTriangle className="adminDataCollection-titleIcon admin-text-warning" />
                Validation Flags
              </span>
            }
            description="Automated data quality checks and issues"
          >
            <div className="adminDataCollection-flagList">
              {validationFlags.map((flag) => (
                <div key={flag.id} className="adminDataCollection-flag">
                  <div className="adminDataCollection-flagBody">
                    <div className="adminDataCollection-flagHead">
                      <strong>
                        {flag.venture_details?.venture_name ?? "-"}
                      </strong>
                      <Badge tone={severityBadge(flag.severity)}>
                        {flag.severity}
                      </Badge>
                    </div>
                    <p className="adminDataCollection-flagMsg">
                      {flag.message}
                    </p>
                    <p className="admin-muted adminDataCollection-flagMeta">
                      {formatDate(flag.date)} • {flag.field}
                    </p>
                  </div>
                  <div className="admin-row">
                    <button
                      type="button"
                      className="admin-btn admin-btn-outline admin-btn-sm"
                      onClick={handleApprove}
                      aria-label="Approve"
                    >
                      <Check className="admin-btn-icon" />
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn-outline admin-btn-sm"
                      onClick={handleFlag}
                      aria-label="Flag"
                    >
                      <Flag className="admin-btn-icon" />
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn-ghost admin-btn-sm"
                      onClick={() =>
                        navigate(
                          `${workspaceBase}/ventures/${flag.venture_details?.venture_id}`,
                        )
                      }
                      aria-label="View venture"
                    >
                      <Eye className="admin-btn-icon" />
                    </button>
                  </div>
                </div>
              ))}
              {validationFlags.length === 0 && (
                <p className="admin-muted">No validation flags available.</p>
              )}
            </div>
          </Card>

          <Card
            title="Audit Trail"
            description="Recent data changes and who made them"
          >
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Venture</th>
                    <th>Field</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {auditTrail.map((entry) => {
                    const firstChange = entry.changes?.[0] ?? {};
                    const ventureName =
                      entry.venture_details?.venture_name || "System";
                    return (
                      <tr key={entry.id}>
                        <td className="admin-muted">
                          {formatDate(entry.date, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td>
                          <strong>{entry.actor?.name ?? "-"}</strong>
                        </td>
                        <td>{entry.action?.name ?? "-"}</td>
                        <td>{ventureName}</td>
                        <td>
                          <Badge tone="outline">
                            {firstChange.field ?? "-"}
                          </Badge>
                        </td>
                        <td>
                          <span className="admin-muted">
                            {firstChange.old_value ?? "-"}
                          </span>
                          <span className="adminDataCollection-arrow">→</span>
                          <strong>{firstChange.new_value ?? "-"}</strong>
                        </td>
                      </tr>
                    );
                  })}
                  {auditTrail.length === 0 && (
                    <tr>
                      <td colSpan="6" className="admin-muted">
                        No audit history available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      <ToastViewport />
    </div>
  );
}

// ---------- helpers ----------

function StreamCard({ icon: Icon, tone, title, value, progress, note }) {
  return (
    <Card>
      <div className="adminDataCollection-streamHead">
        <Icon
          className={`adminDataCollection-streamIcon admin-text-${tone === "warning" ? "warning" : tone === "danger" ? "danger" : ""}`}
        />
        <span className="adminDataCollection-streamTitle">{title}</span>
      </div>
      <div className="adminDataCollection-streamRow">
        <span className="admin-muted">Submitted</span>
        <strong>{value}</strong>
      </div>
      <Progress
        value={progress}
        tone={
          tone === "warning"
            ? "warning"
            : tone === "accent"
              ? "accent"
              : tone === "success"
                ? "success"
                : "primary"
        }
      />
      <p className="admin-muted adminDataCollection-streamNote">{note}</p>
    </Card>
  );
}

function streamTone(title) {
  if (title === "Job Creation") return "accent";
  if (title === "Due Diligence") return "success";
  if (title === "Outcome Surveys") return "warning";
  return "primary";
}
